/* eslint-disable react-native/no-inline-styles,camelcase */
// NOTE: This component is used on *WEB* only!

import { useSideDrawerLeftPresenter } from '@Components/SideDrawerLeft/SideDrawerLeftPresenter';
import useCheckoutPresenter from '@Screens/Checkout/presenter';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React, { useEffect, useState } from 'react';

import { useCheckoutContextState } from '../CheckoutContextState';
import SharedComponents from './SharedComponents';

import { useAnalyticsStore } from '~stores/analytics';
import { useAuthStore } from '~stores/authentication';
import { useCheckoutStore } from '~stores/checkout';
import { useProductsStore } from '~stores/products';
import toastStore from '~stores/toast';

const OrderDetailsStep: FC<object> = () => {
  const {
    checkoutBillingAddress,
    addCardFormVisible,
    setPlaceOrderButtonText,
    setOrderErrorMessage,
    setCheckoutOrderToken,
    CheckoutHandlerSingleton,
    openCheckoutCompleteFlow,
  } = useCheckoutStore();

  const analyticsStore = useAnalyticsStore();

  const [checkoutIsSubmitting, setCheckoutIsSubmitting] =
    useState<boolean>(false);

  const stripe = useStripe();
  const elements = useElements();

  const { activeProductId } = useSideDrawerLeftPresenter();
  const { scrollToBuy, currentProduct } = useProductsStore();
  const { currentUser } = useAuthStore();

  const {
    getOrderSummary,
    setPaymentIntentId,
    setCheckoutOrderId,
    processOrderViaSingletonClass,
  } = useCheckoutPresenter();

  const { orderDetailsVisible } = useCheckoutContextState();
  useEffect(() => {
    (async () => {
      await getOrderSummary(activeProductId);
    })();
    orderDetailsVisible.set(true);

    return () => {
      orderDetailsVisible.set(false);
    };
  }, []);

  // If you make changes here, you also need to make changes in the equivalent .native.tsx file
  // TODO: Suggested improvements for `ProcessOrder`:
  // Move most of the below logic into separate location
  // The only parts which can not be moved are `createOnStripe` and `handle3DSecure`
  // Both those are platform-specific
  // If you're able to manage this move, you can avoid about 80 lines of duplicate code for each platform
  const ProcessOrder = async () => {
    // Handles creating the card on Stripe if necessary
    const createOnStripe = async () => {
      if (activeProductId && stripe) {
        // If the user is a guest, or they're adding a new card, we need to create the payment method on Stripe
        if (addCardFormVisible || !currentUser?.id) {
          const { error: paymentMethodErrors, paymentMethod } =
            await stripe.createPaymentMethod({
              type: 'card',
              card: elements?.getElement(CardElement)!,
              // [FIXME: ] Billing details returns an error when submitting a test card that requires SCA
              billing_details: {
                // addressCity: checkoutBillingAddress.townOrCity ?? "",
                name: checkoutBillingAddress.recipientName,
                address: {
                  city: checkoutBillingAddress.townOrCity,
                  country: 'GB',
                  line1: checkoutBillingAddress.street,
                  postal_code: checkoutBillingAddress.postcode,
                  state: checkoutBillingAddress.county,
                },
              },
            });

          if (paymentMethodErrors) {
            // [FIXME:] Implement error handling for errors
            // alert(paymentMethodErrors.message);
            setPlaceOrderButtonText('Retry order');
            setCheckoutIsSubmitting(false);
            return;
          }
          setPaymentIntentId(paymentMethod?.id);
        }
      }
    };

    // Handles Stripe SCA / 3D Secure
    const handle3DSecure = async () => {
      setOrderErrorMessage(undefined);
      // If there's a client secret present in the singleton, we need to trigger the SCA / 3D Secure prompt
      if (stripe && CheckoutHandlerSingleton.clientSecret) {
        const stripeScaResponse = await stripe.confirmCardPayment(
          CheckoutHandlerSingleton.clientSecret
        );
        const { error } = stripeScaResponse;
        if (error) {
          // [FIXME:] SCA not successful - handle it.
          setPlaceOrderButtonText('Retry order');
          setOrderErrorMessage(
            'Sorry, we were unable to authenticate your card. Please try again.'
          );
          setCheckoutIsSubmitting(false);
          return false;
        }
        return true;
      }
    };

    // Handles authenticating the user order after SCA / 3D Secure is successful
    const handleAuthUserOrder = async () => {
      setOrderErrorMessage(undefined);
      await CheckoutHandlerSingleton.authenticateUserOrder();
      setCheckoutIsSubmitting(false);
      if (CheckoutHandlerSingleton.status === 'PaymentSuccessful') {
        setCheckoutOrderId(CheckoutHandlerSingleton.orderConfirmation?.orderId);
        openCheckoutCompleteFlow();
        CheckoutHandlerSingleton.resetSingletonStatus();
        scrollToBuy();
        setPlaceOrderButtonText('Place order');
      }
    };

    // Handles resubmitting the user order after SCA / 3D Secure fails initially, but is then successful
    const handleResubmitUserOrder = async () => {
      setOrderErrorMessage(undefined);
      await createOnStripe();
      await processOrderViaSingletonClass();
      await CheckoutHandlerSingleton.reconfirmUserOrder({});
      if (CheckoutHandlerSingleton.status === 'PaymentSuccessful') {
        setCheckoutIsSubmitting(false);
        openCheckoutCompleteFlow();
        CheckoutHandlerSingleton.resetSingletonStatus();
        scrollToBuy();
        setPlaceOrderButtonText('Place order');
        return;
      }

      if (CheckoutHandlerSingleton.status === 'PaymentAuthenticationRequired') {
        const stripeResult = await handle3DSecure();
        if (stripeResult) {
          await handleAuthUserOrder();
        }
      }
    };

    setCheckoutIsSubmitting(true);

    if (CheckoutHandlerSingleton.orderConfirmation?.orderId) {
      await handleResubmitUserOrder();
      return;
    }

    await createOnStripe();
    await processOrderViaSingletonClass();
    await CheckoutHandlerSingleton.submitOrder({});
    if (CheckoutHandlerSingleton.status === 'PaymentSuccessful') {
      setCheckoutIsSubmitting(false);
      openCheckoutCompleteFlow();
      setCheckoutOrderToken(CheckoutHandlerSingleton.orderToken);
      setCheckoutOrderId(CheckoutHandlerSingleton.orderConfirmation?.orderId);

      const { postage, orderId, subtotal } =
        CheckoutHandlerSingleton.orderConfirmation ?? {};

      const { productId, productOptionId, quantity } =
        CheckoutHandlerSingleton.ConfirmOrderStructure;
      // @ts-expect-error
      const { name, productType, price } = currentProduct ?? {};

      analyticsStore.logPurchase({
        userId: currentUser?.id,
        postage,
        orderId,
        subtotal,
        productId,
        productName: name,
        productOptionId,
        productType: productType ?? undefined,
        price,
        quantity,
      });

      CheckoutHandlerSingleton.resetSingletonStatus();
      scrollToBuy();
      setPlaceOrderButtonText('Place order');
      return;
    }

    if (CheckoutHandlerSingleton.status === 'PaymentAuthenticationRequired') {
      const stripeResult = await handle3DSecure();
      if (stripeResult) {
        await handleAuthUserOrder();
      } else {
        setPlaceOrderButtonText('Retry order');
        setCheckoutIsSubmitting(false);
      }
      return;
    }

    if (CheckoutHandlerSingleton.status === 'CardDeclined') {
      // TODO: Implement card declined
      toastStore.addMessage({
        message: CheckoutHandlerSingleton.errors?.[0]?.userMessage,
      });
      setPlaceOrderButtonText('Retry order');
      setCheckoutIsSubmitting(false);
      return;
    }

    if (CheckoutHandlerSingleton.status === 'Error') {
      // TODO: Implement error handling
      toastStore.addMessage({
        message: CheckoutHandlerSingleton.errors?.[0]?.userMessage,
      });
      setPlaceOrderButtonText('Retry order');
      setCheckoutIsSubmitting(false);
    }
  };

  return (
    <SharedComponents
      ConfirmOrder={ProcessOrder}
      checkoutIsSubmitting={checkoutIsSubmitting}
    />
  );
};

export default observer(OrderDetailsStep);
